import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, AlertTriangle, Calendar, Ban, Search, CheckCircle2 } from 'lucide-react';
import { validateSepaIban } from '../../../utils/sepa';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper client-side per detection sovrapposizione periodi (mirror della logica backend)
function checkPeriodOverlapClient(tariffs) {
  if (!tariffs || tariffs.length < 2) return null;
  // Costruisce set di tuple (mese, giorno) per ogni periodo (anno bisestile 2024 come riferimento)
  function periodDays(start_dm, end_dm) {
    try {
      const [sd, sm] = start_dm.split('-').map(Number);
      const [ed, em] = end_dm.split('-').map(Number);
      if (!sd || !sm || !ed || !em) return new Set();
      const days = new Set();
      const start = new Date(2024, sm - 1, sd);
      const end = new Date(2024, em - 1, ed);
      const ymdEnd = new Date(2024, 11, 31);
      const ymdStart = new Date(2024, 0, 1);
      const add = (d) => days.add(`${d.getMonth() + 1}-${d.getDate()}`);
      if (start <= end) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) add(new Date(d));
      } else {
        for (let d = new Date(start); d <= ymdEnd; d.setDate(d.getDate() + 1)) add(new Date(d));
        for (let d = new Date(ymdStart); d <= end; d.setDate(d.getDate() + 1)) add(new Date(d));
      }
      return days;
    } catch { return new Set(); }
  }
  const sets = tariffs.map((t, i) => ({ idx: i, name: t.name || `Periodo ${i + 1}`, start: t.start_day_month || '01-01', end: t.end_day_month || '31-12', days: periodDays(t.start_day_month || '01-01', t.end_day_month || '31-12') }));
  for (let i = 0; i < sets.length; i++) {
    for (let j = i + 1; j < sets.length; j++) {
      let intersect = false;
      for (const d of sets[i].days) { if (sets[j].days.has(d)) { intersect = true; break; } }
      if (intersect) {
        return `'${sets[i].name}' (${sets[i].start}→${sets[i].end}) si sovrappone con '${sets[j].name}' (${sets[j].start}→${sets[j].end})`;
      }
    }
  }
  return null;
}

const DEFAULT_RULE = {
  comune_name: '',
  comune_istat_code: '',
  province: '',
  region: '',
  tax_not_applicable: false,
  calculation_type: 'per_person_night',
  seasonal_tariffs: [{ name: 'Tariffa unica', start_day_month: '01-01', end_day_month: '31-12', amount: null, max_nights_taxed: null }],
  default_max_nights_taxed: null,
  exemptions: [],
  payment_method: 'sepa',
  payment_frequency: 'monthly',
  iban: '',
  beneficiary_name: '',
  causale_template: 'Pagamento IDS {nome_alloggio} {periodo}',
  f24_codice_ente: '',
  f24_codice_tributo: '3936',
  f24_sezione: 'EL',
  notes: '',
  is_active: true,
};

export default function RuleFormModal({ rule, prefill, onClose, onSaved }) {
  const isEdit = !!rule;
  const [form, setForm] = useState(() => {
    if (rule) return { ...DEFAULT_RULE, ...rule };
    if (prefill) return { ...DEFAULT_RULE, ...prefill };
    return DEFAULT_RULE;
  });
  const [saving, setSaving] = useState(false);
  const [lookup, setLookup] = useState({ loading: false, found: false, contact_name: '', alternative_count: 0 });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Auto-prefill beneficiary_name + IBAN dal nome comune (lookup rubrica banca)
  useEffect(() => {
    const comune = (form.comune_name || '').trim();
    if (!comune) {
      setLookup({ loading: false, found: false, contact_name: '', alternative_count: 0 });
      return;
    }
    const expected_default = `Comune di ${comune} uff. IDS`;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLookup(prev => ({ ...prev, loading: true }));
      try {
        const token = localStorage.getItem('hw_token');
        const r = await fetch(`${API}/api/tourist-tax/comune-lookup?comune_name=${encodeURIComponent(comune)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (cancelled) return;
        setForm(prev => {
          const updates = {};
          // beneficiary_name: aggiorna SEMPRE se vuoto o se è il default per un comune precedente
          const isDefaultPattern = /^Comune di .+ uff\. IDS$/i.test(prev.beneficiary_name || '');
          if (!prev.beneficiary_name || isDefaultPattern) {
            updates.beneficiary_name = d.suggested_beneficiary || expected_default;
          }
          // IBAN: aggiorna SOLO se vuoto e c'è match
          if (!prev.iban && d.suggested_iban) {
            updates.iban = d.suggested_iban;
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
        setLookup({
          loading: false,
          found: !!d.found,
          contact_name: d.matched_contact_name || '',
          alternative_count: (d.matches || []).length,
        });
      } catch (_) {
        if (!cancelled) setLookup({ loading: false, found: false, contact_name: '', alternative_count: 0 });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.comune_name]);

  const addTariff = () => {
    setForm(prev => ({
      ...prev,
      seasonal_tariffs: [...prev.seasonal_tariffs, { name: 'Periodo', start_day_month: '01-01', end_day_month: '31-12', amount: null, max_nights_taxed: null }]
    }));
  };
  const updateTariff = (i, key, val) => {
    setForm(prev => ({
      ...prev,
      seasonal_tariffs: prev.seasonal_tariffs.map((t, idx) => idx === i ? { ...t, [key]: val } : t)
    }));
  };
  const removeTariff = (i) => {
    setForm(prev => ({ ...prev, seasonal_tariffs: prev.seasonal_tariffs.filter((_, idx) => idx !== i) }));
  };

  const addExemption = () => {
    setForm(prev => ({ ...prev, exemptions: [...prev.exemptions, { type: 'children_under_age', age_threshold: 14, description: 'Bambini sotto i 14 anni' }] }));
  };
  const updateExemption = (i, key, val) => {
    setForm(prev => ({ ...prev, exemptions: prev.exemptions.map((e, idx) => idx === i ? { ...e, [key]: val } : e) }));
  };
  const removeExemption = (i) => {
    setForm(prev => ({ ...prev, exemptions: prev.exemptions.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (!form.comune_name?.trim()) { toast.error('Nome comune obbligatorio'); return; }
    if (!form.tax_not_applicable && form.payment_method === 'sepa' && (!form.iban || !form.beneficiary_name)) {
      toast.error('Per bonifico SEPA servono IBAN e intestazione beneficiario');
      return;
    }
    if (!form.tax_not_applicable && form.payment_method === 'sepa' && form.iban) {
      const validation = validateSepaIban(form.iban);
      if (validation.code === 'INVALID_IBAN') { toast.error(validation.reason); return; }
      if (validation.code === 'EXTRA_SEPA_COUNTRY') toast.warning(validation.reason, { duration: 8000 });
    }
    if (!form.tax_not_applicable && form.payment_method === 'f24' && !(form.f24_codice_ente || '').trim()) {
      toast.error('Per F24 inserisci il Codice Ente del comune (codice catastale)');
      return;
    }
    // Validazione tariffe: tutte devono avere un importo numerico se non è esente
    let payload = { ...form };
    if (!form.tax_not_applicable) {
      const invalid = (form.seasonal_tariffs || []).filter(t => t.amount == null || isNaN(parseFloat(t.amount)));
      if (invalid.length > 0) {
        toast.error('Inserisci un importo valido per ogni tariffa stagionale');
        return;
      }
      // Validazione sovrapposizione periodi (client-side, lato server c'è una seconda barriera)
      const overlapErr = checkPeriodOverlapClient(form.seasonal_tariffs || []);
      if (overlapErr) {
        toast.error(`⚠️ Periodi sovrapposti: ${overlapErr}`);
        return;
      }
    } else {
      // tax_not_applicable: sostituisci null con 0 per compatibilità con backend
      payload = {
        ...form,
        seasonal_tariffs: (form.seasonal_tariffs || []).map(t => ({ ...t, amount: t.amount ?? 0 })),
      };
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('hw_token');
      const url = isEdit ? `${API}/api/tourist-tax/rules/${rule.id}` : `${API}/api/tourist-tax/rules`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Errore (HTTP ${r.status})`);
        return;
      }
      toast.success(isEdit ? 'Regola aggiornata' : 'Regola creata');
      onSaved();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="rule-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifica regola' : 'Nuova regola'} — {form.comune_name || '...'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* DATI COMUNE */}
          <Card className="p-4 bg-slate-50">
            <h4 className="font-semibold mb-3">📍 Dati Comune</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Nome Comune *</label>
                <div className="relative">
                  <Input value={form.comune_name} onChange={e => set('comune_name', e.target.value)} data-testid="comune-name-input" />
                  {lookup.loading && (
                    <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-slate-400" />
                  )}
                </div>
                {form.comune_name && !lookup.loading && (
                  lookup.found ? (
                    <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="comune-lookup-found">
                      <CheckCircle2 className="h-3 w-3" />
                      Trovato in rubrica banca: <strong>{lookup.contact_name}</strong> — IBAN e intestatario precompilati
                    </p>
                  ) : (
                    <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1" data-testid="comune-lookup-not-found">
                      <Search className="h-3 w-3" />
                      Comune non presente in rubrica banca — inserisci IBAN manualmente
                    </p>
                  )
                )}
              </div>
              <div>
                <label className="text-xs font-medium">Codice ISTAT</label>
                <Input value={form.comune_istat_code || ''} onChange={e => set('comune_istat_code', e.target.value)} placeholder="010025" />
              </div>
              <div>
                <label className="text-xs font-medium">Provincia (sigla)</label>
                <Input value={form.province || ''} onChange={e => set('province', e.target.value)} placeholder="GE" maxLength={2} />
              </div>
              <div>
                <label className="text-xs font-medium">Regione</label>
                <Input value={form.region || ''} onChange={e => set('region', e.target.value)} placeholder="Liguria" />
              </div>
            </div>
          </Card>

          {/* TASSA NON APPLICABILE */}
          <Card className={`p-4 border-2 ${form.tax_not_applicable ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-start gap-3">
              <Ban className={`h-6 w-6 mt-0.5 ${form.tax_not_applicable ? 'text-red-600' : 'text-slate-400'}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-slate-900">Comune SENZA tassa di soggiorno</h4>
                    <p className="text-xs text-slate-600 mt-1">
                      Attiva questa opzione se il comune <strong>non ha</strong> la tassa di soggiorno.
                      Gli immobili di questo comune risulteranno <strong>coperti</strong> (non più nella lista "immobili scoperti")
                      ma <strong>non verrà generato alcun report</strong>.
                    </p>
                  </div>
                  <Switch
                    checked={form.tax_not_applicable}
                    onCheckedChange={(v) => set('tax_not_applicable', v)}
                    data-testid="tax-not-applicable-toggle"
                  />
                </div>
                {form.tax_not_applicable && (
                  <div className="mt-3 p-2 bg-white rounded border border-red-200 text-xs text-red-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Tariffe, esenzioni e dati di pagamento sono disabilitati e non verranno salvati.
                  </div>
                )}
              </div>
            </div>
          </Card>

          {!form.tax_not_applicable && (<>
          {/* CALCOLO */}
          <Card className="p-4 bg-blue-50/50">
            <h4 className="font-semibold mb-3">💰 Tipo di Calcolo</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium">Tipo</label>
                <Select value={form.calculation_type} onValueChange={v => set('calculation_type', v)}>
                  <SelectTrigger data-testid="calculation-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_person_night">🌙 Per persona × notte (standard)</SelectItem>
                    <SelectItem value="per_person_booking">💰 Forfettario per persona × prenotazione</SelectItem>
                    <SelectItem value="flat_per_night">🛏️ Forfettario a notti (importo fisso × notte)</SelectItem>
                    <SelectItem value="flat_per_booking">🏠 Forfettario ad alloggio (importo unico × prenotazione)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(form.calculation_type === 'per_person_night' || form.calculation_type === 'flat_per_night') && (
                <div>
                  <label className="text-xs font-medium">Max notti tassabili (default)</label>
                  <Input
                    type="number"
                    value={form.default_max_nights_taxed || ''}
                    onChange={e => set('default_max_nights_taxed', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="es. 8 (Genova) — vuoto = nessun limite"
                    data-testid="max-nights"
                  />
                </div>
              )}
            </div>

            <h5 className="text-sm font-semibold mt-4 mb-2">Tariffe per periodo (alta/bassa stagione)</h5>
            <div className="space-y-2">
              {form.seasonal_tariffs.map((t, i) => {
                const periodCalcType = t.calculation_type || form.calculation_type;
                const hasOverride = !!(t.calculation_type || t.payment_frequency);
                return (
                <div key={i} className={`p-2 bg-white rounded border space-y-2 ${hasOverride ? 'border-emerald-400 ring-1 ring-emerald-200' : ''}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <label className="text-[10px] text-slate-500">Nome periodo</label>
                      <Input value={t.name} onChange={e => updateTariff(i, 'name', e.target.value)} className="h-8" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] text-slate-500">Da (gg-mm)</label>
                      <Input value={t.start_day_month} onChange={e => updateTariff(i, 'start_day_month', e.target.value)} placeholder="01-01" className="h-8" data-testid={`tariff-start-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] text-slate-500">A (gg-mm)</label>
                      <Input value={t.end_day_month} onChange={e => updateTariff(i, 'end_day_month', e.target.value)} placeholder="31-12" className="h-8" data-testid={`tariff-end-${i}`} />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[10px] text-slate-500">€ {
                        periodCalcType === 'per_person_booking' ? '/persona' :
                        periodCalcType === 'per_person_night' ? '/persona/notte' :
                        periodCalcType === 'flat_per_night' ? '/notte' :
                        periodCalcType === 'flat_per_booking' ? '/prenotazione' : ''
                      }</label>
                      <Input
                        type="number"
                        step="0.01"
                        value={t.amount ?? ''}
                        onChange={e => {
                          const v = e.target.value;
                          updateTariff(i, 'amount', v === '' ? null : parseFloat(v));
                        }}
                        placeholder="es. 2.50"
                        className="h-8"
                      />
                    </div>
                    {(periodCalcType === 'per_person_night' || periodCalcType === 'flat_per_night') && (
                      <div className="col-span-2">
                        <label className="text-[10px] text-slate-500">Max notti (override)</label>
                        <Input type="number" value={t.max_nights_taxed || ''} onChange={e => updateTariff(i, 'max_nights_taxed', e.target.value ? parseInt(e.target.value) : null)} className="h-8" />
                      </div>
                    )}
                    <div className="col-span-1">
                      <Button size="sm" variant="outline" className="text-red-600 h-8" onClick={() => removeTariff(i)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  {/* Override per periodo: calc_type + frequency */}
                  <div className="grid grid-cols-12 gap-2 items-end pt-2 border-t border-dashed border-slate-200">
                    <div className="col-span-1 text-[10px] text-slate-500 font-medium pb-2">Override:</div>
                    <div className="col-span-5">
                      <label className="text-[10px] text-slate-500">Tipo Calcolo (vuoto = usa default regola)</label>
                      <Select value={t.calculation_type || '__default__'} onValueChange={v => updateTariff(i, 'calculation_type', v === '__default__' ? null : v)}>
                        <SelectTrigger className="h-8" data-testid={`tariff-calc-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__default__">↩ Usa default regola</SelectItem>
                          <SelectItem value="per_person_night">🌙 Per persona × notte</SelectItem>
                          <SelectItem value="per_person_booking">💰 Forfettario per persona × prenotazione</SelectItem>
                          <SelectItem value="flat_per_night">🛏️ Forfettario a notti</SelectItem>
                          <SelectItem value="flat_per_booking">🏠 Forfettario ad alloggio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5">
                      <label className="text-[10px] text-slate-500">Frequenza Pagamento (vuoto = usa default regola)</label>
                      <Select value={t.payment_frequency || '__default__'} onValueChange={v => updateTariff(i, 'payment_frequency', v === '__default__' ? null : v)}>
                        <SelectTrigger className="h-8" data-testid={`tariff-freq-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__default__">↩ Usa default regola</SelectItem>
                          <SelectItem value="monthly">Mensile</SelectItem>
                          <SelectItem value="bimonthly">Bimestrale</SelectItem>
                          <SelectItem value="quarterly">Trimestrale</SelectItem>
                          <SelectItem value="quadrimonthly">Quadrimestrale</SelectItem>
                          <SelectItem value="semiannual">Semestrale</SelectItem>
                          <SelectItem value="annual">Annuale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      {hasOverride && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Custom</Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
              <Button size="sm" variant="outline" onClick={addTariff} className="text-blue-600">
                <Plus className="h-3 w-3 mr-1" /> Aggiungi periodo
              </Button>
            </div>
          </Card>

          {/* ESENZIONI */}
          <Card className="p-4 bg-emerald-50/50">
            <h4 className="font-semibold mb-3">✅ Esenzioni</h4>
            <div className="space-y-2">
              {form.exemptions.map((e, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 bg-white rounded border">
                  <div className="col-span-3">
                    <label className="text-[10px] text-slate-500">Tipo</label>
                    <Select value={e.type} onValueChange={v => updateExemption(i, 'type', v)}>
                      <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="children_under_age">Bambini sotto età</SelectItem>
                        <SelectItem value="disabled">Disabili</SelectItem>
                        <SelectItem value="tour_operator">Tour operator</SelectItem>
                        <SelectItem value="long_stay">Lunga permanenza</SelectItem>
                        <SelectItem value="other">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {e.type === 'children_under_age' && (
                    <div className="col-span-2">
                      <label className="text-[10px] text-slate-500">Età massima esente</label>
                      <Input type="number" value={e.age_threshold || ''} onChange={ev => updateExemption(i, 'age_threshold', parseInt(ev.target.value) || 0)} className="h-8" />
                    </div>
                  )}
                  <div className={e.type === 'children_under_age' ? 'col-span-6' : 'col-span-8'}>
                    <label className="text-[10px] text-slate-500">Descrizione</label>
                    <Input value={e.description || ''} onChange={ev => updateExemption(i, 'description', ev.target.value)} className="h-8" />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="outline" className="text-red-600 h-8" onClick={() => removeExemption(i)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addExemption} className="text-emerald-600">
                <Plus className="h-3 w-3 mr-1" /> Aggiungi esenzione
              </Button>
            </div>
          </Card>

          {/* PAGAMENTO */}
          <Card className="p-4 bg-amber-50/50">
            <h4 className="font-semibold mb-3">💳 Modalità Pagamento</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Metodo Pagamento</label>
                <Select value={form.payment_method} onValueChange={v => set('payment_method', v)}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sepa">Bonifico SEPA</SelectItem>
                    <SelectItem value="f24">F24</SelectItem>
                    <SelectItem value="pagopa">PagoPA</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Frequenza Pagamento</label>
                <Select value={form.payment_frequency} onValueChange={v => set('payment_frequency', v)}>
                  <SelectTrigger data-testid="payment-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensile</SelectItem>
                    <SelectItem value="bimonthly">Bimestrale</SelectItem>
                    <SelectItem value="quarterly">Trimestrale</SelectItem>
                    <SelectItem value="quadrimonthly">Quadrimestrale</SelectItem>
                    <SelectItem value="semiannual">Semestrale</SelectItem>
                    <SelectItem value="annual">Annuale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.payment_method === 'sepa' && (
                <>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium">IBAN Comune *</label>
                    <Input value={form.iban || ''} onChange={e => set('iban', e.target.value.replace(/\s/g, '').toUpperCase())} placeholder="IT60X0542811101000000123456" data-testid="iban-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium">Intestazione Beneficiario *</label>
                    <Input value={form.beneficiary_name || ''} onChange={e => set('beneficiary_name', e.target.value)} placeholder="Comune di Genova - Servizio Tesoreria" data-testid="beneficiary-input" />
                  </div>
                </>
              )}
              {form.payment_method === 'f24' && (
                <>
                  <div>
                    <label className="text-xs font-medium">Codice Ente Comune *</label>
                    <Input value={form.f24_codice_ente || ''} onChange={e => set('f24_codice_ente', e.target.value.toUpperCase())} placeholder="es. D969 (codice catastale)" data-testid="f24-codice-ente-input" />
                    <p className="text-[10px] text-slate-500 mt-1">Si inserisce una sola volta per comune</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium">Codice Tributo</label>
                      <Input value={form.f24_codice_tributo || '3936'} onChange={e => set('f24_codice_tributo', e.target.value)} data-testid="f24-codice-tributo-input" />
                      <p className="text-[10px] text-slate-500 mt-1">3936 = Imposta di soggiorno</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium">Sezione</label>
                      <Input value={form.f24_sezione || 'EL'} onChange={e => set('f24_sezione', e.target.value.toUpperCase())} data-testid="f24-sezione-input" />
                      <p className="text-[10px] text-slate-500 mt-1">EL = Enti Locali</p>
                    </div>
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <label className="text-xs font-medium">Template Causale Bonifico</label>
                <Input value={form.causale_template} onChange={e => set('causale_template', e.target.value)} placeholder="Pagamento IDS {nome_alloggio} {periodo}" data-testid="causale-template" />
                <p className="text-[10px] text-slate-500 mt-1">
                  Placeholder disponibili: <code>{'{nome_alloggio}'}</code> · <code>{'{periodo}'}</code> · <code>{'{anno}'}</code>
                </p>
              </div>
            </div>
          </Card>
          </>)}

          {/* NOTE */}
          <div>
            <label className="text-xs font-medium">Note libere</label>
            <Textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-rule-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Salva
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
